import getSymbolFromCurrency from "currency-symbol-map";
import Decimal from "decimal.js";
import { EllipsisVertical, Trash2 } from "lucide-react";
import { type KeyboardEvent, memo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { JournalLine } from "@/apis/gql/types";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import { JournalAccountsInput } from "@/components/shared/form/journal-accounts-input";
import {
  ValidationErrorBorder,
  ValidationErrorMessage,
  ValidationRoot,
} from "@/components/shared/form/shared/form-validation";
import { TaxCodeDropdownInput } from "@/components/shared/form/tax-code-dropdown-input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form } from "@/components/ui/form";
import { useCreateJournalLines } from "@/queries/ledger/lines/create";
import { useUpdateJournalLines } from "@/queries/ledger/lines/update";
import { useGetAllTaxCodes } from "@/queries/tax-codes/get-all";

import { AccountEntityType } from "@/lib/constants/account-types";
import { useDeleteJournalLine } from "@/queries/ledger/lines/delete";
import { NavigationElement } from "./navigation";

type BookingLineProps = {
  journalEntryId: string;
  line: JournalLine;
  onRemove: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement | HTMLInputElement>) => void;
  disabled: boolean;
};

const formSchema = z
  .object({
    journalAccountId: z
      .string()
      .min(1, {
        message: "String muss mindestens 1 Zeichen enthalten",
      })
      .max(255),
    debit: z.number().nullable(),
    credit: z.number().nullable(),
    taxCodeId: z.string().max(255).nullable(),
  })
  .refine(
    (data) => {
      const hasDebit = data.debit !== null;
      const hasCredit = data.credit !== null;
      return hasDebit !== hasCredit;
    },
    {
      message: "Soll und Haben dürfen nicht gleichzeitig befüllt sein.",
      path: ["credit"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const getLineAmount = (
  data: FormValues,
): { amount: number; side: "debit" | "credit" } => {
  if (data.debit !== null) {
    return {
      amount: new Decimal(data.debit).times(100).toNumber(),
      side: "debit",
    };
  }

  if (data.credit !== null) {
    return {
      amount: new Decimal(data.credit).times(100).toNumber(),
      side: "credit",
    };
  }

  throw new Error("No amount set");
};

const _BookingLineForm = ({
  journalEntryId,
  line,
  onRemove,
  onKeyDown,
  disabled,
}: BookingLineProps) => {
  const { t } = useTranslation();

  const { taxCodes } = useGetAllTaxCodes();

  const [createLine] = useCreateJournalLines();
  const [updateLine] = useUpdateJournalLines();
  const [deleteLine] = useDeleteJournalLine();

  const form = useForm<FormValues>({
    defaultValues: {
      journalAccountId: line.journalAccount.journalAccountId || "",
      debit: line.debit,
      credit: line.credit,
      taxCodeId: line.taxCodeId || null,
    },
  });

  const suffix = ` ${getSymbolFromCurrency(line.journalAccount.currency || "EUR")}`;

  const isNew = line.journalLineId.startsWith("new-");

  const handleSubmit = async (data: FormValues) => {
    const result = formSchema.safeParse(data);

    if (!result.success) {
      return;
    }

    const { amount, side } = getLineAmount(data);

    if (isNew) {
      await createLine({
        journalEntryId,
        journalAccountId: data.journalAccountId,
        taxCodeId: data.taxCodeId,
        amount,
        side,
        currency: "EUR",
        timestamp: new Date(),
      });
    } else {
      await updateLine({
        journalEntryId,
        journalLineId: line.journalLineId,
        journalAccountId: data.journalAccountId,
        taxCodeId: data.taxCodeId,
        amount,
        side,
        currency: "EUR",
        timestamp: new Date(),
      });
    }
  };

  const handleDelete = async () => {
    if (isNew) {
      onRemove();
      return;
    }

    await deleteLine({
      journalEntryId,
      journalLineId: line.journalLineId,
    });
  };

  const values = form.watch();

  return (
    <Form {...form}>
      <ValidationRoot schema={formSchema} values={values} validateOnDirty>
        {() => (
          <>
            <div className="px-2.5">
              <ValidationErrorBorder className="before:inset-[2px]">
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                  <div
                    key={line.journalLineId}
                    className="grid grid-cols-[minmax(200px,1fr)_140px_140px_140px_auto] gap-2 items-center border-b border-gray-200 last:border-0 py-1"
                  >
                    <div>
                      <NavigationElement>
                        <JournalAccountsInput
                          autoFocus={
                            isNew ||
                            line.journalAccount.entityType ===
                              AccountEntityType.EXPENSE_ACCOUNT
                          }
                          name="journalAccountId"
                          placeholder={t(
                            "transactions.bank_bookings.table.account",
                          )}
                          className="w-full"
                          onKeyDown={onKeyDown}
                          readOnly={disabled}
                        />
                      </NavigationElement>
                    </div>
                    <div className="text-right">
                      <NavigationElement>
                        <FormNumberInput
                          name="debit"
                          suffix={suffix}
                          onKeyDown={onKeyDown}
                          readOnly={disabled}
                        />
                      </NavigationElement>
                    </div>
                    <div className="text-right">
                      <NavigationElement>
                        <FormNumberInput
                          name="credit"
                          suffix={suffix}
                          onKeyDown={onKeyDown}
                          readOnly={disabled}
                        />
                      </NavigationElement>
                    </div>
                    <div>
                      <NavigationElement>
                        <TaxCodeDropdownInput
                          name="taxCodeId"
                          options={taxCodes}
                          placeholder={t(
                            "transactions.bank_bookings.table.tax_code",
                          )}
                          onKeyDown={onKeyDown}
                          modal={false}
                          fullWidth
                          enableClear
                          readOnly={disabled}
                        />
                      </NavigationElement>
                    </div>
                    <div className="flex items-center justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild disabled={disabled}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={disabled}
                          >
                            <EllipsisVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onSelect={handleDelete}
                            className="cursor-pointer gap-2 text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span>{t("delete")}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <AutoSaveForm onSubmit={handleSubmit} debounceTime={100} />
                </form>
              </ValidationErrorBorder>
            </div>
            <div className="flex flex-row gap-0.5 px-4 w-full">
              <div className="flex flex-1">
                <div className="[&:not(:empty)]:mt-1 &:not(:empty)]:mb-1 flex flex-col gap-0.5 px-4">
                  <ValidationErrorMessage name="journalAccountId" />
                  <ValidationErrorMessage name="debit" />
                  <ValidationErrorMessage name="credit" />
                  <ValidationErrorMessage name="taxCodeId" />
                </div>
              </div>
            </div>
          </>
        )}
      </ValidationRoot>
    </Form>
  );
};

export const BookingLineForm = memo(_BookingLineForm);
